import React, { useCallback, useState } from 'react';
import ShipmentColumn from './ShipmentColumn';
import type { ShipmentListItemView } from '../../api/generated/models/ShipmentListItemView';
import type { ShipmentStatusRequest } from '../../api/generated/models/ShipmentStatusRequest';

export const BOARD_STATUSES = [
  'progress',
  'ready_for_departure',
  'in_transit',
  'arrived',
  'clearance_transport',
  'delivered',
] as const;

interface ShipmentBoardProps {
  columns: Record<string, ShipmentListItemView[]>;
  canWrite: boolean;
  onChangeStatus: (id: string, status: ShipmentStatusRequest.executionStatus) => void;
  onArchive: (id: string) => void;
}

const ShipmentBoard: React.FC<ShipmentBoardProps> = ({
  columns,
  canWrite,
  onChangeStatus,
  onArchive,
}) => {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);

  const handleDragStart = useCallback((id: string) => setDraggingId(id), []);
  const handleDragEnd = useCallback(() => { setDraggingId(null); setDragOverStatus(null); }, []);
  const handleDragEnter = useCallback((status: string) => setDragOverStatus(status), []);
  const handleDragLeave = useCallback((status: string) => {
    setDragOverStatus((cur) => (cur === status ? null : cur));
  }, []);

  const handleDrop = useCallback((status: string, id: string) => {
    if (!id) { setDragOverStatus(null); return; }
    const allItems = Object.values(columns).flat();
    const item = allItems.find((s) => s.id === id);
    if (item && item.executionStatus !== status) {
      onChangeStatus(id, status as ShipmentStatusRequest.executionStatus);
    }
    setDraggingId(null);
    setDragOverStatus(null);
  }, [columns, onChangeStatus]);

  return (
    <div className="shipment-board">
      {BOARD_STATUSES.map((status) => (
        <ShipmentColumn
          key={status}
          status={status}
          shipments={columns[status] ?? []}
          canWrite={canWrite}
          draggingId={draggingId}
          isDragOver={dragOverStatus === status}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onChangeStatus={onChangeStatus}
          onArchive={onArchive}
        />
      ))}
    </div>
  );
};

export default ShipmentBoard;
